import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const navLinks = [
  { label: "About", href: "#about" },
  { label: "Projects", href: "#projects" },
  { label: "Blog", href: "#blog" },
  { label: "Testimonials", href: "#testimonials" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onSelectionChange = () => {
      const selection = window.getSelection();
      setHasSelection(Boolean(selection?.toString().trim()));
    };

    document.addEventListener("selectionchange", onSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", onSelectionChange);
  }, []);

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    e.preventDefault();
    setMobileOpen(false);

    if (!href.startsWith("#")) {
      navigate(href);
      return;
    }

    if (isHome) {
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(`/${href}`);
    }
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setMobileOpen(false);
    if (isHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? "bg-white/90 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="section-padding flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a
          href="#"
          onClick={handleLogoClick}
          onMouseEnter={() => setIsLogoHovered(true)}
          onMouseLeave={() => setIsLogoHovered(false)}
          className="flex items-center gap-2 group"
        >
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-revelBlue flex items-center justify-center transition-transform group-hover:scale-110">
            <span className="text-white text-sm md:text-base font-bold leading-none">
              {hasSelection || isLogoHovered ? ":o" : ":)"}
            </span>
          </div>
          <span
            className={`font-bold text-lg md:text-xl tracking-tight transition-colors ${
              scrolled || !isHome ? "text-stem-dark" : "text-white"
            }`}
          >
            Ahmad Danial
          </span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleClick(e, link.href)}
              className={`text-sm font-medium transition-colors hover:text-revelBlue ${
                scrolled || !isHome ? "text-stem-charcoal" : "text-white/90"
              }`}
            >
              {link.label}
            </a>
          ))}
          <a
            href="/contact"
            onClick={(e) => handleClick(e, "/contact")}
            className="btn-dark text-sm py-2.5 px-5"
          >
            Get in Touch
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
            scrolled || !isHome ? "text-stem-dark" : "text-white"
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i
            className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-stem-sand px-4 pb-4 pt-2">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleClick(e, link.href)}
              className="block py-3 text-stem-charcoal font-medium border-b border-stem-sand last:border-0"
            >
              {link.label}
            </a>
          ))}
          <a
            href="/contact"
            onClick={(e) => handleClick(e, "/contact")}
            className="btn-primary w-full justify-center mt-3 text-sm"
          >
            Get in Touch
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      )}
    </nav>
  );
}
